import { initialPagination, initialState, makeListReducers } from '@/models/utils';
import { bindServices } from '@/services/api';
import { getList } from '@/services/global';
import { getFiltersGlobalFilters, toPaginate } from '@/utils/infrastructure';
import CoreQueries from '@/utils/input/queries';

import { DEFAULT_ORDER_BY, FETCH_ROUTE, MODEL_NAME } from './constants';
import { applyExactEmailToFilter, filtersConfig } from './utils';

const api = bindServices(MODEL_NAME);
const pagedQuery = CoreQueries.emailMessageTrace.paged;

const emailEventTracePagination = {
  ...initialPagination,
  limit: 5,
};

/** Pattern B — hand-rolled read-only model adapted to ConfigListPage (`fetchList` / `showForm`). */
const moduleInitialState = {
  ...initialState,
  list: [],
  orderBy: DEFAULT_ORDER_BY,
  select: pagedQuery.select,
  searchSelect: pagedQuery.searchSelect,
  showForm: false,
  detail: false,
  id: null,
  item: null,
  success: false,
  activatingItemIds: {},
  filtersConfig,
  emailEventTraceResult: [],
  emailEventTraceResultPagination: emailEventTracePagination,
  emailEventTraceLoadedId: null,
};

const EmailMessageTraceModel = {
  namespace: MODEL_NAME,
  state: moduleInitialState,
  effects: {
    *fetchList({ payload }, { call, put, select }) {
      try {
        const pagination = yield select((state) => state[MODEL_NAME].pagination);
        const pageSize = yield select((state) => state.global.pageSize);
        const params = applyExactEmailToFilter({
          limit: pageSize,
          ...pagedQuery,
          ...pagination,
          ...payload,
        });
        const res = yield call(api.getAll, {
          route: payload.route ? payload.route : `${FETCH_ROUTE}/paged`,
          params,
          readPriority: true,
        });

        yield put({
          type: 'queryData',
          payload: { ...res, limit: pageSize, page: payload.page },
        });
      } catch (e) {
        /*  */
      }
    },

    *fetchItem({ payload }, { call, put }) {
      try {
        yield put({ type: 'success', payload: false });
        const res = yield call(api.getById, {
          route: FETCH_ROUTE,
          id: payload.id,
          params: {
            ...(payload.detail ? CoreQueries.emailMessageTrace.itemDetail : {}),
            ...(payload?.params && { ...payload.params }),
          },
        });
        yield put({ type: 'queryItem', payload: res });
      } catch (e) {
        /*  */
      }
    },

    *fetchEmailEventTraces({ payload }, { call, put }) {
      try {
        const { emailMessageTraceId, page = 1, limit = 5, orderBy = 'created asc' } = payload;
        const eventHistoryQuery = CoreQueries.emailMessageTrace.eventHistory;

        const res = yield call(getList, {
          route: 'emailEventTrace/paged',
          params: {
            page,
            limit,
            orderBy,
            select: eventHistoryQuery.select,
            filter: `emailMessageTraceId eq ${emailMessageTraceId}`,
          },
        });

        yield put({
          type: 'queryEmailEventTraces',
          payload: { ...res, page, limit, emailMessageTraceId },
        });
      } catch (e) {
        /*  */
      }
    },

    /** Custom export so email exact-match search matches list behavior. */
    *exportList(_, { put, select }) {
      const model = yield select((state) => state[MODEL_NAME]);
      const global = yield select((state) => state.global);
      const filters = getFiltersGlobalFilters(global.globalFilters, MODEL_NAME);
      const params = applyExactEmailToFilter({
        ...(global.searchCriteria && { search: global.searchCriteria }),
        orderBy: model.orderBy || DEFAULT_ORDER_BY,
        ...(filters && { filter: filters }),
        ...(model.select && { select: model.select }),
        ...(model.searchSelect && { searchSelect: model.searchSelect }),
      });

      yield put({
        type: 'global/export',
        payload: {
          route: `${FETCH_ROUTE}/export`,
          httpMethod: 'get',
          ...params,
        },
      });
    },

    *setOrderBy({ payload }, { put }) {
      yield put({
        type: 'queryOrderBy',
        payload,
      });
    },

    *showForm({ payload }, { put }) {
      yield put({
        type: 'queryShowForm',
        payload,
      });
      if (!payload?.state) {
        yield put({ type: 'resetEmailEventTraces' });
      }
    },

    *resetState(_, { put }) {
      yield put({ type: 'reset' });
    },
  },
  reducers: {
    ...makeListReducers({
      initialPagination,
      initialState: moduleInitialState,
    }),
    setFiltersConfig(state, { payload }) {
      return {
        ...state,
        filtersConfig: payload,
      };
    },
    queryEmailEventTraces(state, { payload }) {
      return {
        ...state,
        emailEventTraceResult: payload?.items || [],
        emailEventTraceResultPagination: {
          ...state.emailEventTraceResultPagination,
          ...toPaginate(payload),
        },
        emailEventTraceLoadedId: payload?.emailMessageTraceId ?? state.emailEventTraceLoadedId,
      };
    },
    resetEmailEventTraces(state) {
      return {
        ...state,
        emailEventTraceResult: [],
        emailEventTraceResultPagination: emailEventTracePagination,
        emailEventTraceLoadedId: null,
      };
    },
  },
};

export default EmailMessageTraceModel;
